import type { Decoration, Piece } from "../model/Project";
import type { StampLibrary } from "../stamps/StampLibrary";
import { STITCH_CONNECTION_ICON } from "../model/connections/stitchSpecs";
import eyeOffRaw from "../icons/eye-off.svg?raw";
import eyeRaw from "../icons/eye.svg?raw";
import { stripIconSvg } from "../icons/stripIconSvg";

/** Stitching glyph (currentColor) for the stitch-match connection chip (#199).
 *  Defined once beside the stitch-connection specs (#984) so the chip, the
 *  Properties row, and this kit can't drift onto different icons. */
export const STITCH_MATCH_CHIP_ICON = STITCH_CONNECTION_ICON;

export const DELETE_GLYPH = "×"; // U+00D7 multiplication sign — standard "close" X
export const TRIANGLE_DOWN = "▾";
export const TRIANGLE_RIGHT = "▸";
/** Glyphs for the "expand / collapse all descendants" button (#873). Squared
 *  minus collapses every nested group under the container; squared plus expands
 *  them all. The LayersPanel post-render pass sets the live glyph + label from
 *  whether the subtree is currently all-collapsed, and hides the button when
 *  the container has no collapsible descendants. */
export const COLLAPSE_ALL_GLYPH = "⊟";
export const EXPAND_ALL_GLYPH = "⊞";

/** Build the "expand / collapse all" action button (#873). Its own
 *  `collapse-all-btn` class (NOT `row-action`, which stays unique to the delete
 *  ×) carries the styling + the post-render glyph hook; `stopPropagation` in
 *  its click handler keeps the row from also selecting / toggling. */
function makeCollapseAllButton(onClick: () => void): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.className = "collapse-all-btn";
  btn.textContent = COLLAPSE_ALL_GLYPH;
  btn.title = "Collapse all";
  btn.setAttribute("aria-label", "Collapse all");
  btn.addEventListener("click", (ev) => {
    ev.stopPropagation();
    onClick();
  });
  return btn;
}

interface DecorationBucket {
  /** Composite key (kind + stampId) used for collapsed-state tracking. */
  key: string;
  /** Library stamp name (or fallback) used as the group header label. */
  label: string;
  decorations: Decoration[];
}

/**
 * Groups a piece's decorations by stamp identity. Stamp / lineStamp / fill
 * decorations sharing the same stampId end up in the same bucket; other kinds
 * (offset, freePath) each get their own singleton bucket. First-occurrence
 * order is preserved so the panel reflects placement order.
 */
export function bucketDecorations(
  piece: Piece,
  library: StampLibrary,
): DecorationBucket[] {
  const byKey = new Map<string, DecorationBucket>();
  const ordered: DecorationBucket[] = [];
  for (const dec of piece.decorations) {
    const key = bucketKey(dec);
    if (key === null) {
      ordered.push({
        key: `dec:${dec.id}`,
        label: dec.name,
        decorations: [dec],
      });
      continue;
    }
    let bucket = byKey.get(key);
    if (!bucket) {
      bucket = { key, label: bucketLabel(dec, library), decorations: [] };
      byKey.set(key, bucket);
      ordered.push(bucket);
    }
    bucket.decorations.push(dec);
  }
  return ordered;
}

export function bucketKey(dec: Decoration): string | null {
  if (dec.kind === "stamp" || dec.kind === "lineStamp" || dec.kind === "fill") {
    return `${dec.kind}:${dec.stampId}`;
  }
  return null;
}

export function bucketLabel(dec: Decoration, library: StampLibrary): string {
  if (dec.kind === "stamp" || dec.kind === "lineStamp" || dec.kind === "fill") {
    const meta = library.get(dec.stampId);
    const base = meta?.name ?? "Stamp";
    if (dec.kind === "lineStamp") return `${base} (line)`;
    if (dec.kind === "fill") return `${base} (fill)`;
    return base;
  }
  return dec.name;
}

interface RowOptions {
  /** If set, an interactive triangle toggle is rendered. Otherwise a
   *  same-width spacer keeps leaf rows aligned with collapsible ones. */
  toggleGlyph?: string;
  onToggle?: () => void;
  /** Plain-text glyph for the leading visibility cell (e.g., "●"). */
  leadingGlyph: string;
  /** Optional HTML — used in place of `leadingGlyph` when set (e.g., an
   *  inline SVG of the stamp). */
  leadingHtml?: string;
  /** When true, dims the entire row (icon + label) to indicate the item is
   *  hidden on the canvas. */
  hidden?: boolean;
  label: string;
  selected: boolean;
  indentPx?: number;
  onLeadingClick?: () => void;
  /** Fired on a plain row click. The MouseEvent is forwarded so rows
   *  that participate in multi-select (e.g. construction lines) can
   *  read `shiftKey` / `ctrlKey` / `metaKey` modifiers. Callers that
   *  don't care can ignore the argument. */
  onSelect: (ev: MouseEvent) => void;
  onDoubleClick?: () => void;
  onDelete: () => void;
  /** Optional `data-*` attributes set on the row's outer element.
   *  Used as test hooks (e.g. `data-construction-id`) so e2e
   *  selectors can address rows by stable ids without relying on
   *  child structure. */
  dataAttributes?: Record<string, string>;
  /** When set, an "expand / collapse all descendants" button is rendered to
   *  the left of the delete button (#873). Fires on click; the LayersPanel
   *  decides collapse-vs-expand from the container's current subtree state. */
  onExpandCollapseAll?: () => void;
  /** Optional inline-trailing element rendered between the label
   *  and the row's delete button. Used today by construction
   *  rows to slot constraint chips inside the row itself, so the
   *  chips inherit `.layer-row.selected` styling and live within
   *  the row's hit / hover envelope. */
  trailing?: HTMLElement;
  /** Fires on `mouseenter` / `mouseleave` so callers can paint a
   *  canvas-side hover overlay (yellow halo over the construction
   *  line, etc.) that mirrors which row the pointer is over. The
   *  same handler is invoked with `null` on leave so callers don't
   *  have to wire two separate listeners. */
  onHover?: (entering: boolean) => void;
}

/** Eye / eye-off glyphs for the visibility-toggle hint (#760), read from
 *  `src/icons/eye*.svg` through the shared {@link stripIconSvg} (#216). They
 *  carry no size: each host sizes them (`.visibility-eye svg` in the layers
 *  rows, `.source-visibility-eye svg` in a part's Connections section). */
export const EYE_SVG = stripIconSvg(eyeRaw);
export const EYE_OFF_SVG = stripIconSvg(eyeOffRaw);

/** Add the eye-icon hint (#760) to a `.visibility` cell that toggles hide/show.
 *  The object icon shows normally; on row hover an eye overlays it (a "click to
 *  hide" affordance), and a hidden item shows a persistent slashed eye. Only
 *  applied where the cell actually toggles visibility (`hasToggle`). */
export function addVisibilityHint(
  lead: HTMLElement,
  hidden: boolean,
  hasToggle: boolean,
): void {
  if (!hasToggle) return;
  lead.classList.add("has-visibility-toggle");
  lead.title = hidden ? "Show" : "Hide";
  // Wrap the object icon so CSS can swap it for the eye on hover / when hidden.
  const icon = document.createElement("span");
  icon.className = "visibility-icon";
  while (lead.firstChild) icon.appendChild(lead.firstChild);
  lead.appendChild(icon);
  const eye = document.createElement("span");
  eye.className = "visibility-eye";
  eye.setAttribute("aria-hidden", "true");
  eye.innerHTML = hidden ? EYE_OFF_SVG : EYE_SVG;
  lead.appendChild(eye);
}

/** Double-click detection window (ms) for the layers rows. */
export const ROW_DBLCLICK_MS = 400;
/** Last row click, used to detect a double-click by TIMING + row identity
 *  rather than the native `dblclick` event. A row's first click re-renders the
 *  panel (selection change), REPLACING the row's DOM node — so the two clicks of
 *  a double-click land on different elements and the browser won't synthesise a
 *  `dblclick` (firefox is strict about this; chromium is lenient). Keying off the
 *  row's stable id (its `data-*` attributes) survives the re-render. */
let lastRowClick: { key: string; time: number } | null = null;

/** Extra indent (px) added to every row and group header built inside
 *  {@link withRowIndent} — how a whole subtree nests one level under a section
 *  header without each of its row builders taking a base offset (#1109). */
let rowIndentBase = 0;

/** Build rows one level deeper: every row and header made during `build`
 *  gets `px` more indent on top of its own. Nests, and always restores. */
export function withRowIndent<T>(px: number, build: () => T): T {
  const outer = rowIndentBase;
  rowIndentBase = outer + px;
  try {
    return build();
  } finally {
    rowIndentBase = outer;
  }
}

export function makeRow(opts: RowOptions): HTMLDivElement {
  const row = document.createElement("div");
  row.className = "layer-row";
  if (opts.dataAttributes) {
    for (const [key, value] of Object.entries(opts.dataAttributes)) {
      row.setAttribute(key, value);
    }
  }
  if (opts.selected) row.classList.add("selected");
  if (opts.hidden) row.classList.add("faded");
  const indent = (opts.indentPx ?? 0) + rowIndentBase;
  if (indent) row.style.paddingLeft = `${indent}px`;

  row.appendChild(makeToggle(opts.toggleGlyph, opts.onToggle));

  const lead = document.createElement("span");
  lead.className = "visibility";
  if (opts.leadingHtml !== undefined) {
    lead.innerHTML = opts.leadingHtml;
  } else {
    lead.textContent = opts.leadingGlyph;
  }
  if (opts.onLeadingClick) {
    const cb = opts.onLeadingClick;
    lead.addEventListener("click", (ev) => {
      ev.stopPropagation();
      cb();
    });
  }
  addVisibilityHint(lead, !!opts.hidden, !!opts.onLeadingClick);
  row.appendChild(lead);

  const name = document.createElement("span");
  name.className = "row-label";
  name.textContent = opts.label;
  row.appendChild(name);

  if (opts.onExpandCollapseAll) {
    row.appendChild(makeCollapseAllButton(opts.onExpandCollapseAll));
  }

  const del = document.createElement("button");
  del.className = "row-action";
  del.title = "Delete";
  del.setAttribute("aria-label", "Delete");
  del.textContent = DELETE_GLYPH;
  del.addEventListener("click", (ev) => {
    ev.stopPropagation();
    opts.onDelete();
  });
  row.appendChild(del);

  // Trailing wrap-to-next-line content (e.g. constraint chips on
  // construction rows). Sits *after* the delete button in DOM
  // order with `flex-basis: 100%` in CSS so it forces onto a new
  // line within the row, while still being part of the row's
  // selection / hover envelope.
  if (opts.trailing) row.appendChild(opts.trailing);

  // A stable per-row key for timing-based double-click detection: the row's
  // `data-*` values (which identify the underlying object). Rows without one
  // fall back to the native `dblclick` event below.
  const dblKey = opts.dataAttributes
    ? Object.entries(opts.dataAttributes)
        .map(([k, v]) => `${k}=${v}`)
        .join("|")
    : null;
  const onAction = (target: HTMLElement | null) =>
    !!(
      target?.closest(".row-action") ||
      target?.closest(".visibility") ||
      target?.closest(".row-toggle")
    );

  row.addEventListener("click", (ev) => {
    const dbl = opts.onDoubleClick;
    // A modified click is a multi-select toggle (shift / ⌘ / ctrl), never a
    // double-click-to-zoom — don't let two quick ones pair into one.
    const modified = ev.shiftKey || ev.ctrlKey || ev.metaKey;
    // Detect a double-click by timing when we have a stable key — the panel may
    // re-render (replacing this node) between the two clicks, which kills the
    // native `dblclick` event in strict browsers (firefox). The first click
    // already ran `onSelect`; the second just fires the zoom.
    if (modified) {
      lastRowClick = null;
    } else if (dbl && dblKey && !onAction(ev.target as HTMLElement | null)) {
      const now = Date.now();
      const prev = lastRowClick;
      if (prev && prev.key === dblKey && now - prev.time <= ROW_DBLCLICK_MS) {
        lastRowClick = null;
        dbl();
        return;
      }
      lastRowClick = { key: dblKey, time: now };
    }
    opts.onSelect(ev);
  });
  if (opts.onHover) {
    const cb = opts.onHover;
    row.addEventListener("mouseenter", () => cb(true));
    row.addEventListener("mouseleave", () => cb(false));
  }
  // Native-`dblclick` fallback for rows with no stable key (their DOM node
  // survives, so the native event is reliable enough).
  if (opts.onDoubleClick && !dblKey) {
    const dbl = opts.onDoubleClick;
    row.addEventListener("dblclick", (ev) => {
      if (onAction(ev.target as HTMLElement | null)) return;
      dbl();
    });
  }
  return row;
}

interface GroupHeaderOptions {
  toggleGlyph: string;
  onToggle: () => void;
  /** Optional HTML for the visibility cell — typically a small SVG icon of
   *  the stamp the bucket is built from. */
  leadingHtml?: string;
  /** Click handler on the visibility cell — typically toggles all items in
   *  the bucket between visible / hidden. */
  onLeadingClick?: () => void;
  /** When true, fades the row to indicate every item in the bucket is
   *  hidden. */
  hidden?: boolean;
  /** When true, paints the row with the panel's "selected" styling. */
  selected?: boolean;
  label: string;
  indentPx: number;
  /** Optional click-to-select handler. Distinct from `onToggle` (which is
   *  collapse/expand) — clicking the label area fires both, but the consumer
   *  can react to whichever they care about. The MouseEvent is forwarded so
   *  selectable groups (e.g. construction groups) can read modifier keys. */
  onSelect?: (ev: MouseEvent) => void;
  /** When false, a body click only fires `onSelect` without also toggling
   *  collapse/expand. Default true preserves the original combined behaviour
   *  used by the stitching scope rows. Construction-group headers turn this
   *  off so clicking the row selects the group without surprising it open or
   *  shut — the explicit triangle still toggles. */
  bodyClickToggles?: boolean;
  /** Double-click handler on the label area. Used by construction-group
   *  headers to enter inline rename mode without competing with the single-
   *  click select. */
  onLabelDoubleClick?: () => void;
  onDelete?: () => void;
  /** When set, an "expand / collapse all descendants" button is rendered to
   *  the left of the delete button (#873) — for section / group headers that
   *  nest further collapsible groups. */
  onExpandCollapseAll?: () => void;
  /** Mirror of `RowOptions.onHover` — fires on row mouseenter /
   *  mouseleave so callers can paint a canvas-side hover overlay
   *  while the pointer is over a group header. */
  onHover?: (entering: boolean) => void;
}

export function makeGroupHeader(opts: GroupHeaderOptions): HTMLDivElement {
  const row = document.createElement("div");
  row.className = "layer-row layer-group-header";
  if (opts.hidden) row.classList.add("faded");
  if (opts.selected) row.classList.add("selected");
  // When the header doubles as a selectable target, opt in to the regular
  // row's hover/selection styling so it lights up like the leaf rows do.
  if (opts.onSelect) row.classList.add("selectable");
  row.style.paddingLeft = `${opts.indentPx + rowIndentBase}px`;

  row.appendChild(makeToggle(opts.toggleGlyph, opts.onToggle));

  const lead = document.createElement("span");
  lead.className = "visibility";
  if (opts.leadingHtml !== undefined) lead.innerHTML = opts.leadingHtml;
  if (opts.onLeadingClick) {
    const cb = opts.onLeadingClick;
    lead.addEventListener("click", (ev) => {
      ev.stopPropagation();
      cb();
    });
  }
  addVisibilityHint(lead, !!opts.hidden, !!opts.onLeadingClick);
  row.appendChild(lead);

  const label = document.createElement("span");
  label.className = "row-label";
  label.textContent = opts.label;
  row.appendChild(label);

  if (opts.onExpandCollapseAll) {
    row.appendChild(makeCollapseAllButton(opts.onExpandCollapseAll));
  }

  if (opts.onDelete) {
    const onDelete = opts.onDelete;
    const del = document.createElement("button");
    del.className = "row-action";
    del.title = "Delete all decorations in this group";
    del.setAttribute("aria-label", "Delete group");
    del.textContent = DELETE_GLYPH;
    del.addEventListener("click", (ev) => {
      ev.stopPropagation();
      onDelete();
    });
    row.appendChild(del);
  }

  // Whole row toggles for convenience (clicking the label expands/collapses).
  // When an `onSelect` is provided, clicks also select the row — the order
  // is: ignore action / toggle / visibility cells, otherwise both select and
  // expand. `bodyClickToggles: false` peels the toggle out for headers
  // (e.g. construction groups) where clicking the row should select-only.
  // Clicking the X is a "delete" not a "toggle", so we ignore that.
  const bodyClickToggles = opts.bodyClickToggles ?? true;
  row.addEventListener("click", (ev) => {
    const target = ev.target as HTMLElement | null;
    if (
      target?.closest(".row-action") ||
      target?.closest(".row-toggle") ||
      target?.closest(".visibility")
    ) {
      return;
    }
    opts.onSelect?.(ev);
    if (bodyClickToggles) opts.onToggle();
  });
  if (opts.onLabelDoubleClick) {
    const dbl = opts.onLabelDoubleClick;
    label.addEventListener("dblclick", (ev) => {
      ev.stopPropagation();
      dbl();
    });
  }
  if (opts.onHover) {
    const cb = opts.onHover;
    row.addEventListener("mouseenter", () => cb(true));
    row.addEventListener("mouseleave", () => cb(false));
  }
  return row;
}

export function makeToggle(
  glyph: string | undefined,
  onToggle?: () => void,
): HTMLSpanElement {
  const el = document.createElement("span");
  el.className = "row-toggle";
  el.textContent = glyph ?? "";
  if (glyph && onToggle) {
    el.classList.add("interactive");
    el.addEventListener("click", (ev) => {
      ev.stopPropagation();
      onToggle();
    });
  }
  return el;
}
